import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getPublicBusiness, submitFeedback, mediaUrl } from "@/api";
import { BrandScope } from "@/theme";
import { Button } from "./ui/button";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import bonsteloLogo from "@/assets/brand/bonstelo-logo-full-transparent.png";

const MAX = 1000;

export const FeedbackScreen = () => {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    getPublicBusiness(slug)
      .then((b) => {
        // Feedback module disabled → clean unavailable state (no config disclosure).
        if (b.modules && b.modules.feedback === false) { setNotFound(true); return; }
        setBusiness(b);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async () => {
    if (rating < 1 || busy) return;
    setBusy(true);
    setError("");
    try {
      await submitFeedback(slug, {
        rating,
        improvement_comment: comment.trim() || null,
      });
      setDone(true); // input is preserved in state; only the view swaps out
    } catch (e) {
      const status = e?.response?.status;
      setError(
        status === 429
          ? "Es wurden gerade sehr viele Rückmeldungen gesendet. Bitte versuche es später noch einmal."
          : "Dein Feedback konnte gerade nicht gesendet werden. Bitte versuche es erneut."
      );
    } finally {
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <div data-testid="feedback-not-found" className="min-h-screen flex items-center justify-center p-6 text-center bg-slate-50">
        <div>
          <p className="font-display font-extrabold text-xl text-slate-900">Betrieb nicht gefunden</p>
          <p className="text-slate-500 text-sm mt-1">Dieser Link ist ungültig.</p>
        </div>
      </div>
    );

  if (!business)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );

  const logo = mediaUrl(business.logo_url);
  const initial = business.name?.[0] || "B";

  return (
    <BrandScope primary={business.primary_color} secondary={business.secondary_color} className="App">
      <div data-testid="feedback-screen" className="min-h-[100dvh] bg-slate-50 flex flex-col">
        <div className="w-full max-w-md mx-auto flex-1 flex flex-col px-5 pt-6 pb-4">
          {/* Business identity */}
          <div className="flex flex-col items-center text-center shrink-0">
            <div className="h-16 w-16 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
              {logo ? (
                <img src={logo} alt={business.name} className="h-full w-full object-contain" />
              ) : (
                <span className="font-display font-extrabold text-2xl" style={{ color: "var(--brand-primary)" }}>{initial}</span>
              )}
            </div>
            <h1 className="font-display font-extrabold text-xl mt-2 tracking-tight" style={{ color: "var(--brand-primary)" }}>
              {business.name}
            </h1>
          </div>

          {done ? (
            <div data-testid="feedback-success" className="flex-1 flex flex-col items-center justify-center text-center py-6">
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <p className="font-display font-extrabold text-xl text-slate-900 mt-5">Danke für dein Feedback.</p>
              <p className="text-slate-500 text-sm font-medium mt-1.5">Deine Rückmeldung wurde übermittelt.</p>
              <p className="text-slate-400 text-sm font-medium mt-3">Du kannst dieses Fenster jetzt schließen.</p>
            </div>
          ) : (
            <div className="mt-6">
              <h2 className="font-display font-extrabold text-lg text-slate-900 text-center leading-snug">
                Dein Feedback hilft uns, besser zu werden.
              </h2>
              <p className="text-slate-500 text-sm font-medium mt-2 text-center leading-relaxed">
                Sag uns gern, was wir verbessern können. Deine Rückmeldung ist anonym, bleibt intern und wird nicht öffentlich angezeigt.
              </p>

              {/* Q1 — required rating */}
              <div className="mt-6">
                <p className="font-display font-bold text-slate-800 text-center">Wie zufrieden warst du?</p>
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = (hover || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        data-testid={`feedback-star-${n}`}
                        aria-label={`${n} Sterne`}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setRating(n)}
                        className="p-1.5 press rounded-xl focus:outline-none"
                      >
                        <Star
                          className="h-10 w-10 transition-colors"
                          style={{ color: active ? "var(--brand-secondary)" : "#cbd5e1" }}
                          fill={active ? "var(--brand-secondary)" : "none"}
                          strokeWidth={active ? 1.5 : 2}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Q2 — optional comment */}
              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <p className="font-display font-bold text-slate-800">Was können wir verbessern?</p>
                  <span className="text-xs font-semibold text-slate-400">Optional</span>
                </div>
                <textarea
                  data-testid="feedback-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, MAX))}
                  maxLength={MAX}
                  rows={3}
                  placeholder="Deine Anregungen …"
                  className="mt-2 w-full rounded-2xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-[var(--brand-primary)] outline-none px-4 py-3 text-base text-slate-800 resize-none"
                />
                <div className="mt-1 text-right text-[11px] font-medium text-slate-300">{comment.length}/{MAX}</div>
              </div>

              {error && (
                <p data-testid="feedback-error" className="text-rose-600 text-sm font-semibold text-center mt-2">{error}</p>
              )}

              <Button
                data-testid="feedback-submit-button"
                onClick={submit}
                disabled={rating < 1 || busy}
                className="w-full mt-4 py-3.5 rounded-2xl font-display font-bold press text-white disabled:opacity-50"
                style={{ background: "var(--brand-primary)" }}
              >
                {busy ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Senden …</> : "Feedback senden"}
              </Button>
            </div>
          )}

          <div className="mt-auto pt-6 pb-[env(safe-area-inset-bottom)] flex items-center justify-center gap-1.5 text-slate-300 shrink-0">
            <span className="font-semibold text-[11px]">Powered by</span>
            <img src={bonsteloLogo} alt="Bonstelo" className="h-4 w-auto opacity-70" />
          </div>
        </div>
      </div>
    </BrandScope>
  );
};
